import React from "react";
import { ArrowLeft, ArrowRight, Check, Info } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "../../ui/tabs";
import { useTranslation } from "../../../hooks/useTranslation";

// Temi predefiniti — esadecimali esatti dagli swatch Figma (frame 4018:1420)
export const COLOR_THEMES = [
  { id: "aziendale", nameKey: "themeAziendale", descKey: "themeAziendaleDesc", primary: "#2563EA", secondary: "#64748B", text: "#0F182B" },
  { id: "moderno", nameKey: "themeModerno", descKey: "themeModernoDesc", primary: "#8B5CF6", secondary: "#4D1D95", text: "#0F182B" },
  { id: "caldo", nameKey: "themeCaldo", descKey: "themeCaldoDesc", primary: "#F97216", secondary: "#FB933C", text: "#471302" },
  { id: "natura", nameKey: "themeNatura", descKey: "themeNaturaDesc", primary: "#10B982", secondary: "#34D399", text: "#023A2C" },
  { id: "corporateDark", nameKey: "themeCorporateDark", descKey: "themeCorporateDarkDesc", primary: "#0F172A", secondary: "#64748B", text: "#020617" },
  { id: "neutroLight", nameKey: "themeNeutroLight", descKey: "themeNeutroLightDesc", primary: "#E2E8F0", secondary: "#64748B", text: "#020617" },
];

const ColorField = ({ label, value, onChange }) => (
  <div className="flex-1 min-w-[140px]">
    <Label className="text-xs font-medium">{label}</Label>
    <div className="mt-1.5 flex items-center gap-2">
      <input
        type="color"
        value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : "#000000"}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 w-9 shrink-0 cursor-pointer rounded-md border bg-transparent p-0.5"
        aria-label={label}
      />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 font-mono text-xs"
        maxLength={7}
        spellCheck={false}
      />
    </div>
  </div>
);

const ThemeCard = ({ theme, selected, onSelect, t }) => (
  <button
    type="button"
    onClick={onSelect}
    className={`p-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
      selected
        ? "rounded-[9px] border-2 border-[#3F8DFF] bg-[#3F8DFF]/10"
        : "rounded-lg border border-[#CBD5E1] bg-white hover:border-[#3F8DFF]/60"
    }`}
  >
    <div className="flex items-start gap-2.5">
      <span
        className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border ${
          selected
            ? "border-[#3F8DFF] bg-[#3F8DFF] text-white"
            : "border-[#CBD5E1] bg-white"
        }`}
      >
        {selected && <Check className="h-3 w-3" />}
      </span>
      <div className="min-w-0">
        <p className="text-sm font-semibold leading-tight">
          {t(`onboardingWizard.${theme.nameKey}`)}
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {t(`onboardingWizard.${theme.descKey}`)}
        </p>
        <div className="mt-2 flex gap-1.5">
          {[theme.primary, theme.secondary, theme.text].map((c) => (
            <span
              key={c}
              className="h-4 w-4 rounded-[4px] border border-black/10"
              style={{ backgroundColor: c }}
            />
          ))}
        </div>
      </div>
    </div>
  </button>
);

/**
 * Step 1 del wizard custom — "Personalizza il tuo Bot"
 * (frame 4002:8045 + 4018:746): identità, messaggio di benvenuto,
 * logo e colori (brand liberi o tema predefinito).
 */
const StepPersonalization = ({ config, updateConfig, onNext, onBack }) => {
  const { t } = useTranslation();

  const selectTheme = (theme) => {
    updateConfig({
      themeId: theme.id,
      primaryColor: theme.primary,
      secondaryColor: theme.secondary,
      textColor: theme.text,
    });
  };

  return (
    <Card className="rounded-[14px] shadow-[0_5px_5px_rgba(0,0,0,0.1)]">
      <CardHeader>
        <CardTitle className="text-xl">
          {t("onboardingWizard.step1Title")}
        </CardTitle>
        <CardDescription>
          {t("onboardingWizard.step1Subtitle")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div>
          <Label className="text-sm">
            {t("onboardingWizard.botNameLabel")} *
          </Label>
          <Input
            className="mt-1.5"
            value={config.name}
            onChange={(e) => updateConfig({ name: e.target.value })}
            placeholder={t("onboardingWizard.botNamePlaceholder")}
            maxLength={100}
          />
        </div>

        <div>
          <Label className="text-sm">
            {t("onboardingWizard.subtitleLabel")}
          </Label>
          <Input
            className="mt-1.5"
            value={config.subtitle}
            onChange={(e) => updateConfig({ subtitle: e.target.value })}
            placeholder={t("onboardingWizard.subtitlePlaceholder")}
            maxLength={80}
          />
        </div>

        <div>
          <Label className="text-sm">
            {t("onboardingWizard.welcomeLabel")}
          </Label>
          <Textarea
            className="mt-1.5 min-h-[72px]"
            value={config.welcomeMessage}
            onChange={(e) => updateConfig({ welcomeMessage: e.target.value })}
            placeholder={t("onboardingWizard.welcomePlaceholder")}
            maxLength={400}
          />
        </div>

        <div>
          <Label className="text-sm">{t("onboardingWizard.logoLabel")}</Label>
          <div className="mt-1.5 flex">
            <span className="flex items-center rounded-l-md border border-r-0 bg-muted px-3 text-sm text-muted-foreground">
              https://
            </span>
            <Input
              className="rounded-l-none"
              value={config.logoUrl.replace(/^https?:\/\//, "")}
              onChange={(e) =>
                updateConfig({
                  logoUrl: e.target.value
                    ? `https://${e.target.value.replace(/^https?:\/\//, "")}`
                    : "",
                })
              }
              placeholder={t("onboardingWizard.logoPlaceholder")}
            />
          </div>
          {/* Testo del design (frame 4002:8045), con icona info: riusa le
              stesse chiavi dello step sito web. */}
          <p className="mt-1.5 flex items-start gap-1.5 text-xs text-muted-foreground">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              {t("onboardingWizard.websiteHint1")}
              <br />
              {t("onboardingWizard.websiteHint2")}
            </span>
          </p>
        </div>

        <Tabs
          value={config.colorMode}
          onValueChange={(v) => updateConfig({ colorMode: v })}
        >
          <TabsList>
            <TabsTrigger value="brand">
              {t("onboardingWizard.tabBrandColors")}
            </TabsTrigger>
            <TabsTrigger value="theme">
              {t("onboardingWizard.tabThemes")}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {config.colorMode === "brand" ? (
          <div>
            <p className="text-sm text-muted-foreground">
              {t("onboardingWizard.brandColorsHint")}
            </p>
            <div className="mt-4 flex flex-wrap gap-4">
              <ColorField
                label={t("onboardingWizard.primaryColor")}
                value={config.primaryColor}
                onChange={(v) => updateConfig({ primaryColor: v, themeId: null })}
              />
              <ColorField
                label={t("onboardingWizard.secondaryColor")}
                value={config.secondaryColor}
                onChange={(v) => updateConfig({ secondaryColor: v, themeId: null })}
              />
              <ColorField
                label={t("onboardingWizard.textColor")}
                value={config.textColor}
                onChange={(v) => updateConfig({ textColor: v, themeId: null })}
              />
            </div>
            <p className="mt-3 flex items-start gap-1.5 text-xs text-muted-foreground">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {t("onboardingWizard.colorFieldsHint")}
            </p>
          </div>
        ) : (
          <div>
            <p className="text-sm text-muted-foreground">
              {t("onboardingWizard.themesHint")}
            </p>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {COLOR_THEMES.map((theme) => (
                <ThemeCard
                  key={theme.id}
                  theme={theme}
                  selected={config.themeId === theme.id}
                  onSelect={() => selectTheme(theme)}
                  t={t}
                />
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-2">
          <Button type="button" variant="secondary" onClick={onBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("onboardingWizard.back")}
          </Button>
          <Button
            type="button"
            onClick={onNext}
            disabled={!config.name.trim()}
          >
            {t("onboardingWizard.continue")}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default StepPersonalization;
